"use client";

import { useEffect, useRef, useState } from "react";

// Legora-style stat block (2026-06-05): a light two-column section — short intro
// on the left, a vertical stack of big numbers on the right separated by thin
// rules. Each number counts up from 0 the first time it scrolls into view.
// SSR renders the final value (SEO / no-JS); reduced-motion shows finals, no
// animation; on motion-capable clients we reset to 0 off-screen, then count up.
type Stat =
  | { value: number; suffix?: string; label: string }
  | { text: string; label: string };

const STATS: Stat[] = [
  {
    value: 25,
    suffix: "+",
    label: "Official UK data sources, from the ICO to the courts",
  },
  {
    value: 9,
    label: "Litigation categories in scope, from data breach to competition",
  },
  {
    text: "Same day",
    label: "From a filing or notice to a ranked, citation-backed signal",
  },
];

function Counter({ value, suffix = "" }: { value: number; suffix?: string }) {
  // Initial = final value so SSR / no-JS / reduced-motion show the real number.
  const [n, setN] = useState(value);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Reset to 0 (the block is below the fold here, so this is invisible), then
    // count up the first time it scrolls into view.
    setN(0);
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        let start: number | null = null;
        const tick = (ts: number) => {
          start ??= ts;
          const p = Math.min((ts - start) / 900, 1);
          setN(Math.round(p * value));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

export function StatBand() {
  return (
    <section className="bg-background">
      <div className="mx-auto grid w-full max-w-[1240px] gap-14 px-6 py-24 sm:px-10 sm:py-28 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
        {/* left: intro */}
        <div className="lg:pt-3">
          <span className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent before:h-px before:w-7 before:bg-accent before:content-['']">
            By the numbers
          </span>
          <h2 className="mt-6 max-w-[420px] font-sans text-[28px] font-medium leading-[1.15] tracking-[-0.01em] text-foreground sm:text-[34px]">
            Wide enough to catch the signal. Focused enough to rank it.
          </h2>
          <p className="mt-5 max-w-[400px] text-[16px] leading-relaxed text-muted-foreground">
            From the UK&rsquo;s regulators and registries to the courts, Thexis
            watches the official record and surfaces what is worth acting on.
          </p>
        </div>

        {/* right: stacked count-up stats */}
        <div>
          {STATS.map((s, i) => (
            <div
              key={i}
              className="border-t border-border py-8 first:border-t-0 first:pt-0 sm:py-10"
            >
              <div className="font-sans text-[54px] font-medium leading-[0.95] tracking-[-0.03em] text-foreground sm:text-[72px]">
                {"value" in s ? (
                  <Counter value={s.value} suffix={s.suffix} />
                ) : (
                  s.text
                )}
              </div>
              <div className="mt-3 max-w-[440px] text-[15px] leading-snug text-muted-foreground">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
